import React, { useState, useEffect, useMemo } from 'react';
import styled from "@emotion/styled";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery
} from "@mui/material";
import { TabPanel } from '@mui/lab';
import { CheckCircleOutline, Clear, Delete, Edit, Refresh, Search, WarningAmber } from '@mui/icons-material';
import { getFixedPricePayments, updateFixedPricePayment, deleteFixedPricePayment, clearFixedPricePayments } from '../redux/ducks/fixedPricePayments';
import AppDatePicker from './AppDatePicker';
import { addDays, formatDayShort, formatDate, toLocalDay } from '../utils/dateUtils';

const PaymentsTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: 0,
    paddingTop: theme.spacing(3),
}));

const ToolbarRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

const SearchField = styled(TextField)({
    flex: 1,
    minWidth: 220,
});

const StatusSelect = styled(TextField)({
    minWidth: 180,
});

const EditFormStack = styled(Stack)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
}));

const StatusCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const StatusDot = styled(Box, { shouldForwardProp: (prop) => prop !== 'status' })(({ theme, status }) => {
    const palette = {
        Paid: theme.palette.success.main,
        Pending: theme.palette.text.disabled,
        Due: theme.palette.warning.main,
        Draft: theme.palette.warning.main,
        Open: theme.palette.info.main,
        Overdue: theme.palette.error.main,
        Cancelled: theme.palette.error.main,
        Completed: theme.palette.success.main,
    };
    return {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: palette[status] || theme.palette.text.disabled,
    };
});

const ParentLink = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    cursor: "pointer",
    color: theme.palette.primary.main,
    display: "inline-block",
    "&:hover": {
        color: theme.palette.primary.dark,
        textDecoration: "underline",
    },
}));

const ParentTypeChip = styled(Chip)(({ theme }) => ({
    marginLeft: theme.spacing(1),
    height: 18,
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
}));

const EmptyTableCell = styled(TableCell)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const SummaryStrip = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing(1.5),
    },
}));

const MobileCardList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const PaymentRowCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5),
}));

const CardTopRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
}));

const CardClient = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    overflowWrap: 'break-word',
}));

const CardMetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
    flexWrap: 'wrap',
}));

const CardActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
}));

const CardAmount = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const EmptyMobileState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4, 2),
    textAlign: 'center',
}));

const SummaryStat = styled(Box)(({ theme }) => ({
    paddingLeft: theme.spacing(1.5),
    borderLeft: `2px solid ${theme.palette.divider}`,
}));

const STATUS_OPTIONS = ['All', 'Unpaid', 'Orphan', 'Pending', 'Due', 'Draft', 'Open', 'Overdue', 'Paid', 'Cancelled'];

const formatPaymentDate = (date) => formatDayShort(date) || '—';

const formatCurrency = (n) => `$${(n || 0).toFixed(2)}`;

const isUnpaid = (status) => status !== 'Paid' && status !== 'Cancelled' && status !== 'Completed';

const BillingFixedPricePayments = ({ tabValue }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [payments, setPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('Unpaid');
    const [editing, setEditing] = useState(null);
    const [paying, setPaying] = useState(null);
    const [paidDate, setPaidDate] = useState('');
    const [editDate, setEditDate] = useState('');
    const [editStatus, setEditStatus] = useState('Pending');
    const [editPercentage, setEditPercentage] = useState('');
    const [editRelativeDate, setEditRelativeDate] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    const loadPayments = () => {
        setIsLoading(true);
        dispatch(getFixedPricePayments((response) => {
            setIsLoading(false);
            setPayments(response?.payments || []);
        }));
    };

    useEffect(() => {
        if (tabValue === 'payments') {
            loadPayments();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue]);

    const filtered = useMemo(() => {
        return payments.filter((p) => {
            if (statusFilter === 'Orphan' && !p.orphan) return false;
            if (statusFilter === 'Unpaid' && !isUnpaid(p.status)) return false;
            if (!['All', 'Unpaid', 'Orphan'].includes(statusFilter) && p.status !== statusFilter) return false;
            if (search) {
                const haystack = [
                    p.client?.name,
                    p.account?.name,
                    p.parent?.name
                ].filter(Boolean).join(' ').toLowerCase();
                if (!haystack.includes(search)) return false;
            }
            return true;
        });
    }, [payments, statusFilter, search]);

    const summary = useMemo(() => {
        const today = toLocalDay();
        const stats = { total: payments.length, unpaidValue: 0, dueSoonCount: 0, overdueCount: 0, paidLast90: 0, paidLast90Value: 0 };
        const horizon = toLocalDay(addDays(new Date(), 30));
        const cutoff = toLocalDay(addDays(new Date(), -90));
        for (const p of payments) {
            if (isUnpaid(p.status)) {
                stats.unpaidValue += p.value || 0;
                const day = formatDate(p.date);
                if (day) {
                    if (day < today) stats.overdueCount += 1;
                    else if (day <= horizon) stats.dueSoonCount += 1;
                }
            } else if (p.status === 'Paid' && p.paidDate && formatDate(p.paidDate) >= cutoff) {
                stats.paidLast90 += 1;
                stats.paidLast90Value += p.value || 0;
            }
        }
        return stats;
    }, [payments]);

    const dueThreshold = toLocalDay(addDays(new Date(), -32));

    const handlePaymentClick = (p) => {
        if (p.parent?.type === 'Project' && p.account?.slug) {
            navigate(`/account/${p.account.slug}/project/${p.parent._id}`);
        } else if (p.parent?.type === 'Group' && p.account?.slug) {
            navigate(`/account/${p.account.slug}`);
        }
    };

    const openEdit = (p) => {
        if (!p.editable) return;
        setEditing(p);
        setEditDate(formatDate(p.date));
        setEditStatus(p.status);
        setEditPercentage(p.percentage != null ? String(p.percentage) : '');
        setEditRelativeDate(p.relativeDate || '');
    };

    const closeEdit = () => {
        if (isSaving) return;
        setEditing(null);
    };

    const applyPaymentUpdate = (target, body, onDone) => {
        const paymentKey = target._id || target.paymentNumber;
        dispatch(updateFixedPricePayment(target.parent.type, target.parent._id, paymentKey, body, (response) => {
            if (response?.payment) {
                setPayments((prev) => prev.map((p) => (
                    (p._id === target._id || (!p._id && p.paymentNumber === target.paymentNumber)) &&
                    p.parent._id === target.parent._id
                        ? { ...p, ...response.payment }
                        : p
                )));
            }
            if (onDone) onDone(!!response?.payment);
        }));
    };

    const [deleting, setDeleting] = useState(null);
    const closeDelete = () => { if (!isSaving) setDeleting(null); };

    const submitDelete = () => {
        if (!deleting) return;
        setIsSaving(true);
        dispatch(deleteFixedPricePayment(deleting.parent.type, deleting.parent._id, deleting._id || deleting.paymentNumber, (ok) => {
            setIsSaving(false);
            setDeleting(null);
            if (ok) {
                setPayments((prev) => prev.filter((p) =>
                    !(p.parent._id === deleting.parent._id && (p._id === deleting._id || (!p._id && p.paymentNumber === deleting.paymentNumber)))
                ));
            }
        }));
    };

    const handleClearAll = (p) => {
        setIsSaving(true);
        dispatch(clearFixedPricePayments(p.parent.type, p.parent._id, (ok) => {
            setIsSaving(false);
            if (ok) {
                setPayments((prev) => prev.filter((row) => row.parent._id !== p.parent._id));
            }
        }));
    };

    const submitEdit = () => {
        if (!editing) return;
        const body = {
            date: editRelativeDate ? null : (editDate || null),
            status: editStatus,
            percentage: editPercentage === '' ? undefined : Number(editPercentage),
            relativeDate: editRelativeDate || ''
        };
        setIsSaving(true);
        applyPaymentUpdate(editing, body, (ok) => {
            setIsSaving(false);
            if (ok) setEditing(null);
        });
    };

    const openMarkPaid = (p) => {
        if (!p.editable) return;
        setPaying(p);
        setPaidDate(toLocalDay());
    };

    const closeMarkPaid = () => {
        if (isSaving) return;
        setPaying(null);
    };

    const submitMarkPaid = () => {
        if (!paying || !paidDate) return;
        setIsSaving(true);
        applyPaymentUpdate(paying, { status: 'Paid', paidDate, date: paying.date || paidDate }, (ok) => {
            setIsSaving(false);
            if (ok) setPaying(null);
        });
    };


    return (
        <PaymentsTabPanel value="payments">
            <Card>
                <CardHeader
                    title="Fixed Price Payments"
                    subheader="Unpaid milestones plus payments collected in the last 90 days"
                    action={
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Refresh />}
                            onClick={loadPayments}
                            disabled={isLoading}
                        >
                            Refresh
                        </Button>
                    }
                />
                <CardContent>
                    <SummaryStrip>
                        <SummaryStat>
                            <Typography variant="caption" color="text.secondary">Tracked</Typography>
                            <Typography variant="h6">{summary.total}</Typography>
                        </SummaryStat>
                        <SummaryStat>
                            <Typography variant="caption" color="text.secondary">Unpaid value</Typography>
                            <Typography variant="h6">{formatCurrency(summary.unpaidValue)}</Typography>
                        </SummaryStat>
                        <SummaryStat>
                            <Typography variant="caption" color="text.secondary">Overdue</Typography>
                            <Typography variant="h6">{summary.overdueCount}</Typography>
                        </SummaryStat>
                        <SummaryStat>
                            <Typography variant="caption" color="text.secondary">Due in 30 days</Typography>
                            <Typography variant="h6">{summary.dueSoonCount}</Typography>
                        </SummaryStat>
                        <SummaryStat>
                            <Typography variant="caption" color="text.secondary">Collected (90d)</Typography>
                            <Typography variant="h6">{formatCurrency(summary.paidLast90Value)}</Typography>
                        </SummaryStat>
                    </SummaryStrip>

                    <ToolbarRow>
                        <SearchField
                            size="small"
                            placeholder="Search by client, account, or project"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchInput('')}>
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null
                            }}
                        />
                        <StatusSelect
                            select
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </StatusSelect>
                    </ToolbarRow>

                    {isLoading ? (
                        <CenteredLoading>
                            <CircularProgress />
                        </CenteredLoading>
                    ) : isMobile ? (
                        filtered.length === 0 ? (
                            <EmptyMobileState>
                                <Typography variant="body2" color="text.secondary">
                                    No payments match the current filters
                                </Typography>
                            </EmptyMobileState>
                        ) : (
                            <MobileCardList>
                                {filtered.map((p) => {
                                    const rowKey = `${p.parent.type}-${p.parent._id}-${p._id || `n${p.paymentNumber}`}`;
                                    const dueColor = dueThreshold <= formatDate(p.date) ? 'text.secondary' : 'error';
                                    return (
                                        <PaymentRowCard key={rowKey} variant="outlined">
                                            <CardTopRow>
                                                <Box minWidth={0}>
                                                    <CardClient variant="body2">
                                                        {p.client?.name || p.account?.name || '—'}
                                                    </CardClient>
                                                    {p.client?.name && p.account?.name && p.client.name !== p.account.name && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {p.account.name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <StatusCell>
                                                    <StatusDot status={p.status} />
                                                    <Typography variant="caption">{p.status}</Typography>
                                                </StatusCell>
                                            </CardTopRow>
                                            <Box display="flex" alignItems="baseline" flexWrap="wrap" gap={0.75}>
                                                <ParentLink
                                                    variant="body2"
                                                    component="span"
                                                    onClick={() => handlePaymentClick(p)}
                                                >
                                                    {p.parent?.name || '—'}
                                                </ParentLink>
                                                <ParentTypeChip
                                                    label={p.parent?.type}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                {p.orphan && (
                                                    <Chip
                                                        icon={<WarningAmber fontSize="small" />}
                                                        label={`Orphan — ${p.parent?.billingType || 'non-FP'}`}
                                                        size="small"
                                                        color="warning"
                                                    />
                                                )}
                                            </Box>
                                            <CardMetaRow>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Payment #{p.paymentNumber}
                                                        {p.percentage != null ? ` · ${p.percentage}%` : ''}
                                                    </Typography>
                                                    <Typography variant="body2" color={p.status === 'Paid' ? 'text.secondary' : dueColor}>
                                                        {p.status === 'Paid'
                                                            ? `Collected ${formatPaymentDate(p.paidDate)}`
                                                            : `${formatPaymentDate(p.date)}${!p.date && p.relativeDate ? ` (on ${p.relativeDate})` : ''}`}
                                                    </Typography>
                                                </Box>
                                                <CardAmount variant="body1">{formatCurrency(p.value)}</CardAmount>
                                            </CardMetaRow>
                                            <CardActions>
                                                {!p.orphan && (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openMarkPaid(p)}
                                                            disabled={!p.editable}
                                                            title={p.editable ? 'Mark as Paid' : `Locked: status is ${p.status}`}
                                                        >
                                                            <CheckCircleOutline fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEdit(p)}
                                                            disabled={!p.editable}
                                                            title={p.editable ? 'Edit payment' : `Locked: status is ${p.status}`}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeleting(p)}
                                                    disabled={p.status === 'Paid'}
                                                    title={p.status === 'Paid' ? 'Cannot delete a paid payment' : 'Delete payment'}
                                                    color={p.orphan ? 'warning' : 'default'}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </CardActions>
                                        </PaymentRowCard>
                                    );
                                })}
                            </MobileCardList>
                        )
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Client / Account</TableCell>
                                        <TableCell>Project / Group</TableCell>
                                        <TableCell>Payment</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filtered.length === 0 ? (
                                        <TableRow>
                                            <EmptyTableCell colSpan={7} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    No payments match the current filters
                                                </Typography>
                                            </EmptyTableCell>
                                        </TableRow>
                                    ) : (
                                        filtered.map((p) => {
                                            const rowKey = `${p.parent.type}-${p.parent._id}-${p._id || `n${p.paymentNumber}`}`;
                                            return (
                                                <TableRow key={rowKey} hover>
                                                    <TableCell>
                                                        <ListItemText
                                                            disableTypography
                                                            primary={
                                                                <Typography variant="body2">
                                                                    {p.client?.name || p.account?.name || '—'}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                p.client?.name && p.account?.name && p.client.name !== p.account.name
                                                                    ? <Typography variant="caption" color="text.secondary">{p.account.name}</Typography>
                                                                    : null
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <ParentLink
                                                            variant="body2"
                                                            component="span"
                                                            onClick={() => handlePaymentClick(p)}
                                                        >
                                                            {p.parent?.name || '—'}
                                                        </ParentLink>
                                                        <ParentTypeChip
                                                            label={p.parent?.type}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        {p.orphan && (
                                                            <Chip
                                                                icon={<WarningAmber fontSize="small" />}
                                                                label={`Orphan — ${p.parent?.billingType || 'non-FP'}`}
                                                                size="small"
                                                                color="warning"
                                                                sx={{ ml: 1 }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">#{p.paymentNumber}</Typography>
                                                        {p.percentage != null && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {p.percentage}%
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">{formatCurrency(p.value)}</TableCell>
                                                    <TableCell>
                                                        <Stack spacing={0}>
                                                            <Typography variant="body2" color={p.status === 'Paid' || dueThreshold <= formatDate(p.date) ? "text.secondary" : "error"}>
                                                                {p.status === 'Paid' ? `Collected ${formatPaymentDate(p.paidDate)}` : formatPaymentDate(p.date)}
                                                            </Typography>
                                                            {!p.date && p.relativeDate && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    On {p.relativeDate}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <StatusCell>
                                                            <StatusDot status={p.status} />
                                                            <Typography variant="body2">{p.status}</Typography>
                                                        </StatusCell>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {!p.orphan && (
                                                            <>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => openMarkPaid(p)}
                                                                    disabled={!p.editable}
                                                                    title={p.editable ? 'Mark as Paid' : `Locked: status is ${p.status}`}
                                                                >
                                                                    <CheckCircleOutline fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => openEdit(p)}
                                                                    disabled={!p.editable}
                                                                    title={p.editable ? 'Edit payment' : `Locked: status is ${p.status}`}
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setDeleting(p)}
                                                            disabled={p.status === 'Paid'}
                                                            title={p.status === 'Paid' ? 'Cannot delete a paid payment' : 'Delete payment'}
                                                            color={p.orphan ? 'warning' : 'default'}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={!!editing} onClose={closeEdit} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {editing ? `Edit payment #${editing.paymentNumber}` : ''}
                </DialogTitle>
                <DialogContent>
                    {editing && (
                        <EditFormStack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {editing.parent?.name} — {formatCurrency(editing.value)}
                            </Typography>
                            <AppDatePicker
                                label="Date"
                                size="small"
                                value={editRelativeDate ? '' : editDate}
                                onChange={(v) => setEditDate(v)}
                                disabled={!!editRelativeDate}
                                helperText={
                                    editRelativeDate === 'start'
                                        ? "Follows the parent's start date"
                                        : editRelativeDate === 'due'
                                            ? "Follows the parent's due date"
                                            : "When this payment is due"
                                }
                            />
                            <TextField
                                select
                                label="Relative date"
                                size="small"
                                fullWidth
                                value={editRelativeDate}
                                onChange={(e) => setEditRelativeDate(e.target.value)}
                                helperText="Optional: anchor the payment to the parent's start or due date"
                            >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="start">Start of parent</MenuItem>
                                <MenuItem value="due">Due date of parent</MenuItem>
                            </TextField>
                            <TextField
                                select
                                label="Status"
                                size="small"
                                fullWidth
                                value={editStatus}
                                onChange={(e) => setEditStatus(e.target.value)}
                                helperText={
                                    editStatus === 'Paid'
                                        ? 'Marks this milestone as collected and removes it from the unpaid list'
                                        : editStatus === 'Cancelled'
                                            ? 'Cancels this milestone — it will no longer appear in unpaid totals'
                                            : ' '
                                }
                            >
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Due">Due</MenuItem>
                                <MenuItem value="Paid">Paid</MenuItem>
                                <MenuItem value="Cancelled">Cancelled</MenuItem>
                            </TextField>
                            <TextField
                                label="Percentage"
                                type="number"
                                size="small"
                                fullWidth
                                value={editPercentage}
                                onChange={(e) => setEditPercentage(e.target.value)}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                helperText="Share of the parent's total payment"
                            />
                        </EditFormStack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEdit} disabled={isSaving}>Cancel</Button>
                    <Button variant="contained" onClick={submitEdit} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={18} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!paying} onClose={closeMarkPaid} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {paying ? `Record payment #${paying.paymentNumber} as collected` : ''}
                </DialogTitle>
                <DialogContent>
                    {paying && (
                        <EditFormStack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {paying.client?.name ? `${paying.client.name} — ` : ''}{paying.parent?.name} — {formatCurrency(paying.value)}
                            </Typography>
                            <AppDatePicker
                                label="Date collected"
                                size="small"
                                value={paidDate}
                                onChange={(v) => setPaidDate(v)}
                                helperText="Defaults to today. Change it if the money arrived earlier — this is the date the revenue is reported against."
                            />
                            <Typography variant="caption" color="text.secondary">
                                Marks the milestone Paid and removes it from the unpaid list. Use this for payments that arrive
                                outside an invoice, such as a cheque — invoiced milestones are recorded when the invoice is paid.
                            </Typography>
                        </EditFormStack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeMarkPaid} disabled={isSaving}>Cancel</Button>
                    <Button variant="contained" onClick={submitMarkPaid} disabled={isSaving || !paidDate}>
                        {isSaving ? <CircularProgress size={18} /> : 'Mark as Paid'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={!!deleting} onClose={closeDelete} maxWidth="xs" fullWidth>
                <DialogTitle>Delete payment</DialogTitle>
                <DialogContent>
                    {deleting && (
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                            <Typography variant="body2">
                                Payment #{deleting.paymentNumber} on <strong>{deleting.parent?.name}</strong> ({formatCurrency(deleting.value)})
                            </Typography>
                            {deleting.orphan && (
                                <Typography variant="body2" color="warning.main">
                                    This payment is orphaned: the parent is {deleting.parent?.billingType || 'non-Fixed Price'}, not Fixed Price. It should not exist and will cause billing errors if left in place.
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                                This cannot be undone. If other payments remain on the same parent they will keep their current values.
                            </Typography>
                            {deleting.orphan && (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    onClick={() => { handleClearAll(deleting); setDeleting(null); }}
                                    disabled={isSaving}
                                >
                                    Clear all payments on this {deleting.parent?.type?.toLowerCase()}
                                </Button>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDelete} disabled={isSaving}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={submitDelete} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={18} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PaymentsTabPanel>
    );
};

export default BillingFixedPricePayments;
